import type { ImmotionAttachment } from "@myapp/contract";
import { API_URL } from "../config/axios";
import { describeFile, type FileDescriptor, type FileKind } from "./fileType";
import { attachmentByteSize, createAttachmentUrl } from "./formatBytes";

export interface ViewAttachment {
  key: string;
  source: "app" | "immotion";
  /** `${API_URL}${path}` for app attachments, a `blob:` URL for Immotion ones, null if undecodable. */
  url: string | null;
  descriptor: FileDescriptor;
  /** "Foto 2" / "Dokument 1" — generic on purpose: app attachments carry no real filename, only a
   *  UUID the API renamed the upload to, so a human name does not exist to show. */
  label: string;
  /** Known for Immotion attachments (derived from the base64 length); null for app attachments. */
  sizeBytes: number | null;
}

/** The bucket name a kind's counter and label share — pdf and doc are both "Dokument". */
const LABEL_BY_KIND: Record<FileKind, string> = {
  image: "Foto",
  pdf: "Dokument",
  doc: "Dokument",
  sheet: "Tabelle",
  archive: "Archiv",
  other: "Datei",
};

/**
 * Normalises the two unrelated attachment sources the appointment page has — app attachments
 * (bare `/static/…` path strings, no mime, no size) and Immotion attachments (inline base64 with
 * a real mime and filename) — into one list, in a stable per-kind label sequence shared across
 * both sources. The task page has only the first source; `immotionAttachments` is simply omitted.
 *
 * Lives in `shared/lib` (moved from `pages/worker-appointment/model`) so both worker pages can use
 * it — FSD forbids a page importing from another page.
 */
export function buildViewAttachments(
  appAttachments: string[] | null | undefined,
  immotionAttachments: ImmotionAttachment[] | null | undefined,
): ViewAttachment[] {
  const counts = new Map<string, number>();
  const nextLabel = (kind: FileKind): string => {
    const bucket = LABEL_BY_KIND[kind];
    const n = (counts.get(bucket) ?? 0) + 1;
    counts.set(bucket, n);
    return `${bucket} ${n}`;
  };

  const fromApp: ViewAttachment[] = (appAttachments ?? []).map((path, i) => {
    const descriptor = describeFile({ url: path });
    return {
      key: `app-${i}`,
      source: "app",
      url: `${API_URL}${path}`,
      descriptor,
      label: nextLabel(descriptor.kind),
      sizeBytes: null,
    };
  });

  const fromImmotion: ViewAttachment[] = (immotionAttachments ?? []).map((attachment, i) => {
    const descriptor = describeFile({ mime: attachment.mime, filename: attachment.filename });
    return {
      key: `immotion-${attachment.filename}-${i}`,
      source: "immotion",
      url: createAttachmentUrl(attachment),
      descriptor,
      label: nextLabel(descriptor.kind),
      sizeBytes: attachmentByteSize(attachment),
    };
  });

  return [...fromApp, ...fromImmotion];
}
